import { Model, modelRegistry } from './Model';
import { Product } from '.';
import HashGen from '../utilities/HashGen';
import Money from '../utilities/Money';
import { I18n } from '../wrappers/I18n';
export class PriceCalculationItem extends Model {
    static entity = 'price_calculation_items';
    id = null;
    price_calculation_id = null;
    date = null;
    product_id = null;
    unit_of_measure = null;
    tax_rate = '0.00';
    quantity = '1.000';
    invoice_date = null;
    invoice_price = '0.0000';
    invoice_discount_percent = '0.00';
    invoice_with_tax = true;
    dependent_costs = '0.0000';
    selling_price = '0.0000';
    selling_price_after_tax = '0.0000';
    margin_percent = '0.00';
    recorded_at = null;
    created_at = null;
    updated_at = null;
    hash = '';
    static fields() {
        return {
            id: this.attr(null),
            price_calculation_id: this.attr(null),
            date: this.attr(null),
            product_id: this.attr(null),
            unit_of_measure: this.attr(null),
            tax_rate: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            quantity: this.attr('1.000', value => this.round(value, 3).toFixed(3)),
            invoice_date: this.attr(null),
            invoice_price: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            invoice_discount_percent: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            invoice_with_tax: this.attr(true, value => !!value),
            dependent_costs: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            selling_price: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            selling_price_after_tax: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            margin_percent: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            recorded_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            hash: this.attr(HashGen.regular()),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    getTaxRate() {
        return parseFloat(this.tax_rate);
    }
    getQuantity() {
        return parseFloat(this.quantity);
    }
    getInvoicePrice() {
        return Money.parse(this.invoice_price);
    }
    getInvoiceDiscountPercent() {
        return parseFloat(this.invoice_discount_percent);
    }
    getInvoicePriceAfterDiscount() {
        const value = this.getInvoicePrice()
            .multiply(1 - this.getInvoiceDiscountPercent() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    getInvoiceValue() {
        const value = this.getInvoicePriceAfterDiscount()
            .multiply(this.getQuantity());
        return Money.parse(value.toFormat('0.0000'));
    }
    getDependentCosts() {
        return Money.parse(this.dependent_costs);
    }
    getPurchaseValue() {
        const value = this.getInvoiceValue()
            .add(this.getDependentCosts());
        return Money.parse(value.toFormat('0.0000'));
    }
    getPurchaseTax() {
        if (!this.invoice_with_tax)
            return Money.parse(0);
        const value = this.getPurchaseValue()
            .multiply(this.getTaxRate() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    getPurchaseValueAfterTax() {
        const value = this.getPurchaseValue()
            .add(this.getPurchaseTax());
        return Money.parse(value.toFormat('0.0000'));
    }
    getPriceDifference() {
        return this.getSellingValue()
            .subtract(this.getPurchaseValue());
    }
    getPriceDifferencePerUnit() {
        const value = this.getQuantity() != 0
            ? this.getPriceDifference()
                .divide(this.getQuantity())
            : Money.parse(0);
        return Money.parse(value.toFormat('0.0000'));
    }
    getSellingValue() {
        const value = this.getSellingPrice()
            .multiply(this.getQuantity());
        return Money.parse(value.toFormat('0.0000'));
    }
    getTax() {
        const value = this.getSellingValue()
            .multiply(this.getTaxRate() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    getSellingValueAfterTax() {
        const value = this.getSellingValue()
            .add(this.getTax());
        return Money.parse(value.toFormat('0.0000'));
    }
    getPurchasePrice() {
        const value = this.getQuantity() != 0
            ? this.getPurchaseValue()
                .divide(this.getQuantity())
            : Money.parse(0);
        return Money.parse(value.toFormat('0.0000'));
    }
    getSellingPrice() {
        return Money.parse(this.selling_price);
    }
    getSellingPriceAfterTax() {
        return Money.parse(this.selling_price_after_tax);
    }
    /**
     * The lowest selling price the line accepts: its purchase price (below it the margin is
     * negative), to the 4 decimals the prices are kept in — rounded to 2, 1.6667 became 1.67
     * and refused the zero-margin price.
     */
    getMinSellingPrice() {
        return this.getPurchasePrice().toRoundedUnit(4);
    }
    /**
     * The lowest after-tax selling price the line accepts: the lowest one whose before-tax price,
     * as calculatePrices derives it, is not below the purchase price. The purchase price times
     * the tax, rounded, can sit a ten-thousandth above it (21%: 1.0331 → 1.2501, yet 1.25 gives
     * back 1.0331) and would refuse a line sold at cost.
     */
    getMinSellingPriceAfterTax() {
        const minSellingPrice = this.getMinSellingPrice();
        const step = Money.parse('0.0001');
        const sellingPriceOf = (afterTax) => Number(this.calculatePrices(afterTax.toUnit(), 'selling_price_after_tax').selling_price);
        // At or above the floor; the derivation only rounds, so this is at most a few steps above it.
        let min = Money.parse(Money.parse(minSellingPrice).multiply(1 + this.getTaxRate() / 100).toFormat('0.0000'));
        while (sellingPriceOf(min.subtract(step)) >= minSellingPrice)
            min = min.subtract(step);
        return min.toRoundedUnit(4);
    }
    getMarginPercent() {
        return parseFloat(this.margin_percent);
    }
    getProduct() {
        return Product.find(this.product_id);
    }
    getUnitOfMeasure() {
        return I18n.t(`products.unit_of_measure.${this.unit_of_measure}`) !== `products.unit_of_measure.${this.unit_of_measure}`
            ? I18n.t(`products.unit_of_measure.${this.unit_of_measure}`)
            : this.unit_of_measure;
    }
    calculatePrices(value, baseField) {
        let sellingPrice = Money.parse(0);
        let sellingPriceAfterTax = Money.parse(0);
        let marginPercent = Money.parse(0);
        if (baseField === 'selling_price') {
            sellingPrice = Money.parse(this.round(value, 4));
            sellingPriceAfterTax = Money.parse(sellingPrice.toUnit())
                .multiply(1 + this.getTaxRate() / 100);
            marginPercent = this.getPurchasePrice().toUnit() != 0
                ? Money.parse(sellingPrice.toUnit())
                    .subtract(this.getPurchasePrice())
                    .divide(this.getPurchasePrice().toUnit())
                    .multiply(100)
                : Money.parse(0);
        }
        else if (baseField === 'selling_price_after_tax') {
            sellingPriceAfterTax = Money.parse(this.round(value, 4));
            sellingPrice = Money.parse(sellingPriceAfterTax.toUnit())
                .divide(1 + this.getTaxRate() / 100);
            marginPercent = this.getPurchasePrice().toUnit() != 0
                ? Money.parse(sellingPrice.toUnit())
                    .subtract(this.getPurchasePrice())
                    .divide(this.getPurchasePrice().toUnit())
                    .multiply(100)
                : Money.parse(0);
        }
        else if (baseField === 'margin_percent') {
            marginPercent = Money.parse(this.round(value, 2));
            sellingPrice = this.getPurchasePrice()
                .multiply(1 + marginPercent.toUnit() / 100);
            sellingPriceAfterTax = Money.parse(sellingPrice.toUnit())
                .multiply(1 + this.getTaxRate() / 100);
        }
        return {
            selling_price: sellingPrice.toFormat('0.0000'),
            selling_price_after_tax: sellingPriceAfterTax.toFormat('0.0000'),
            margin_percent: marginPercent.toFormat('0.00'),
        };
    }
    calculateDependentCosts(totalInvoicePrice, totalDependentCosts) {
        if (!totalInvoicePrice || !totalDependentCosts)
            return '0.0000';
        const invoicePrice = this.round(totalInvoicePrice, 4);
        const dependentCosts = this.round(totalDependentCosts, 4);
        if (!invoicePrice || !dependentCosts)
            return '0.0000';
        const value = this.getInvoiceValue()
            .divide(invoicePrice)
            .multiply(dependentCosts);
        return value.toFormat('0.0000');
    }
}
export default PriceCalculationItem;
modelRegistry.register('PriceCalculationItem', PriceCalculationItem);
//# sourceMappingURL=PriceCalculationItem.js.map